import { useEffect, useState } from "react";
import { fnbAPI, fmtINR, fmtINRshort, fmtDate } from "./fnbApi";
import CountUp from "@/pages/operations/CountUp";
import {
  Utensils, Wine, Waves, Bed, ChefHat, Users,
  Grid3x3, Sunrise, ShoppingCart, TrendingUp,
} from "lucide-react";

const DarkTile = ({ label, value, icon: Icon, accent = "text-hocp-gold", testid, sub }) => (
  <div data-testid={testid}
    className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
    <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
    <div className={`w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center ${accent} relative`}>
      <Icon className="w-5 h-5" strokeWidth={1.75}/>
    </div>
    <div className="mt-4 relative">
      <div className="font-display text-[24px] font-semibold text-white tabular-nums leading-none">{value}</div>
      <div className="text-[11px] text-slate-200 font-medium mt-2 uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  </div>
);

const LightTile = ({ label, value, icon: Icon, testid, iconBg = "bg-sky-50 text-sky-700" }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testid}>
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center mb-2.5`}>
      <Icon className="w-4 h-4" strokeWidth={1.75}/>
    </div>
    <div className="font-display text-[22px] font-semibold text-hocp-navy tabular-nums leading-none">{value}</div>
    <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">{label}</div>
  </div>
);

export default function FnbDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => fnbAPI.dashboard().then(setD).catch(() => {});
    load(); const t = setInterval(load, 20000); return () => clearInterval(t);
  }, []);
  const k = d?.kpis || {};
  return (
    <div className="space-y-5" data-testid="fnb-dashboard">
      {/* HERO */}
      <div className="on-dark relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1020] via-[#070B1F] to-[#0F1530] p-6 border border-white/10">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-hocp-gold/10 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-fuchsia-500/10 blur-3xl pointer-events-none"/>
        <div className="relative">
          <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
            <Utensils className="w-3.5 h-3.5"/> Business Date
            <span className="text-hocp-gold font-semibold tabular-nums" data-testid="fnb-business-date">{d?.business_date || "—"}</span>
          </div>
          <div className="mt-3 font-display text-[42px] font-bold text-white tabular-nums leading-none" data-testid="fnb-total-revenue">
            {fmtINR(k.total_revenue)}
          </div>
          <div className="text-[13px] text-slate-300 mt-2">
            Today&apos;s F&amp;B revenue · {k.orders_today || 0} orders · Avg ticket {fmtINR(k.avg_ticket)}
          </div>
        </div>
      </div>

      {/* Dark KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <DarkTile label="Restaurant"    value={fmtINRshort(k.restaurant_revenue)}   icon={Utensils} testid="fnb-kpi-rest"  accent="text-orange-300"/>
        <DarkTile label="Pool"          value={fmtINRshort(k.pool_revenue)}         icon={Waves}    testid="fnb-kpi-pool"  accent="text-cyan-300"/>
        <DarkTile label="Room Service"  value={fmtINRshort(k.room_service_revenue)} icon={Bed}      testid="fnb-kpi-rs"    accent="text-violet-300"/>
        <DarkTile label="Bar Counter"   value={fmtINRshort(k.bar_counter_revenue)}  icon={Wine}     testid="fnb-kpi-bar"   accent="text-fuchsia-300"/>
        <DarkTile label="Avg Ticket"    value={fmtINRshort(k.avg_ticket)}           icon={TrendingUp} testid="fnb-kpi-avg" accent="text-emerald-300"/>
        <DarkTile label="Orders Today"  value={<CountUp value={Number(k.orders_today)||0}/>} icon={ShoppingCart} testid="fnb-kpi-orders" accent="text-sky-300"/>
      </div>

      {/* Light ops tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <LightTile label="Open Tables"   value={<CountUp value={Number(k.open_tables)||0}/>}       icon={Grid3x3}  testid="fnb-kpi-open-tables" iconBg="bg-sky-50 text-sky-700"/>
        <LightTile label="Avail. Tables" value={<CountUp value={Number(k.available_tables)||0}/>}   icon={Grid3x3}  testid="fnb-kpi-avail-tables" iconBg="bg-emerald-50 text-emerald-700"/>
        <LightTile label="Kitchen Queue" value={<CountUp value={Number(k.kitchen_queue)||0}/>}      icon={ChefHat}  testid="fnb-kpi-kq" iconBg="bg-orange-50 text-orange-700"/>
        <LightTile label="Bar Queue"     value={<CountUp value={Number(k.bar_queue)||0}/>}          icon={Wine}     testid="fnb-kpi-bq" iconBg="bg-fuchsia-50 text-fuchsia-700"/>
        <LightTile label="RS Open"       value={<CountUp value={Number(k.room_service_open)||0}/>}  icon={Bed}      testid="fnb-kpi-rso" iconBg="bg-violet-50 text-violet-700"/>
        <LightTile label="Pool Open"     value={<CountUp value={Number(k.pool_open)||0}/>}          icon={Waves}    testid="fnb-kpi-po" iconBg="bg-cyan-50 text-cyan-700"/>
        <LightTile label="Bkfst Expected" value={<CountUp value={Number(k.breakfast_expected)||0}/>} icon={Sunrise} testid="fnb-kpi-be" iconBg="bg-amber-50 text-amber-700"/>
        <LightTile label="Bkfst Served"  value={<CountUp value={Number(k.breakfast_served)||0}/>}   icon={Users}    testid="fnb-kpi-bs" iconBg="bg-emerald-50 text-emerald-700"/>
      </div>

      {/* Recent orders */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-display text-[15px] font-semibold text-hocp-navy">Recent Orders</h2>
        </div>
        {!d?.recent_orders?.length ? (
          <div className="p-8 text-center text-[13px] text-slate-400" data-testid="fnb-no-orders">No orders today yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Where</th>
                <th className="text-left px-4 py-2.5">Guest</th>
                <th className="text-right px-4 py-2.5">Items</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.recent_orders.slice(-8).reverse().map(o => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`fnb-recent-${o.code}`}>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-hocp-navy">{o.code}</td>
                  <td className="px-4 py-2.5 text-slate-700 capitalize">{o.service_location_kind?.replace("_"," ")}</td>
                  <td className="px-4 py-2.5 text-slate-700">{o.guest_name || <span className="text-slate-400 italic">—</span>}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{o.lines?.length || 0}</td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums text-hocp-navy">{fmtINR(o.total)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${
                      o.status === "closed" ? "bg-emerald-100 text-emerald-700" :
                      o.status === "open" ? "bg-sky-100 text-sky-700" :
                      "bg-rose-100 text-rose-700"}`}>{o.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-[12px]">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
